import React, { useEffect, useState } from 'react';
import { Check, Music, Download, Play, Star, Ban } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isFavorited, toggleFavorite, favoritesChangeEvent } from '../utils/favorites';
import { useResolvedPortrait, speakerIsGalleryless } from '../hooks/useSpeakerPortrait';
import useCoverPool, { poolCount } from '../hooks/useCoverPool';
import { prefetchRecording } from '../utils/prefetch';
import { getListenStatus } from '../utils/resume';
import { getSpeakerPaletteScene } from '../utils/speakerPalette';
import { downloadRecording } from '../utils/downloadRecording';
import {
  getShowSpeakerPortraitsSetting,
  SPEAKER_PORTRAITS_SETTING_EVENT,
} from '../pages/SettingsPage';
// Feb-2026 (user): Email-me-this-shiur icon removed from the recording
// card hover — email is still available via the Share menu inside the
// player page. EmailLibraryModal import stays out of this file since it
// no longer has a trigger here.
// (Add-to-playlist icon moved to AudioPlayer — only appears once user opens
//  a recording, so card grid stays clean.)

/**
 * Shrink Drive thumbnail requests so grids of dozens of cards don't fetch
 * 800px images each. Keeps compatibility with non-drive URLs.
 * Feb-2026: bumped from sz=w320 → sz=w640 because 320 was noticeably soft
 * on 3× retina phones (was rendering ~320 device-px into a ~390 device-px
 * tile). 640 costs ~2× bandwidth per tile but reads sharp on every DPR.
 */
const smallerThumb = (url) => {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/sz=w\d+/, 'sz=w640');
};

const RecordingCard = ({ recording, preferSpeakerPortrait = true, queue = null }) => {
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // RULE 19 (Feb-2026): when we're rendering ON the speaker's OWN
  // /speaker/{name} page AND that speaker has no gallery-folder of
  // pics, his recording covers MUST be the translucent cutout. In
  // every OTHER context (search, topic pages, home, favorites) his
  // recordings fall back to the unedited (rule-16) original portrait.
  const onOwnSpeakerPage = (() => {
    const m = location.pathname.match(/^\/speaker\/([^/]+)/);
    if (!m) return false;
    try {
      const routeSpeaker = decodeURIComponent(m[1]);
      return routeSpeaker === (recording?.speaker || '');
    } catch {
      return false;
    }
  })();
  const insideOwnSpeakerAndGalleryless =
    onOwnSpeakerPage && speakerIsGalleryless(recording?.speaker);
  // Respect the Settings toggle: when the user has turned OFF "show speaker
  // photos on recording tiles", we fall through to plain cover images
  // everywhere, no matter what the caller asks for.
  const [portraitsEnabled, setPortraitsEnabled] = useState(getShowSpeakerPortraitsSetting);
  useEffect(() => {
    const sync = () => setPortraitsEnabled(getShowSpeakerPortraitsSetting());
    window.addEventListener(SPEAKER_PORTRAITS_SETTING_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SPEAKER_PORTRAITS_SETTING_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const effectivePrefer = preferSpeakerPortrait && portraitsEnabled;
  // PERMANENT RULE (Feb 2026): every recording MUST render SOMETHING
  // recognizable as the speaker on the tile. Historically this used
  // `useResolvedPortrait(effectivePrefer ? recording : null)` which
  // returned `null` on the speaker's own page (preferSpeakerPortrait=
  // false). That's why "R' Shalom Muskat — As the Yom Hadin Approaches"
  // (and every other recording of his that lives outside the covers
  // folder) rendered a generic Music icon: he has a single portrait in
  // his speaker profile and no cover folder, so cover_image was null
  // and the portrait lookup was intentionally skipped.
  //
  // FIX: always resolve the speaker portrait. If cover art exists, use
  // it (rotating covers on the speaker page); otherwise fall back to
  // the speaker's own portrait so his face still shows. DO NOT revert
  // to the conditional lookup — it silently breaks every speaker with
  // no picture folder.
  const portraitUrl = useResolvedPortrait(portraitsEnabled ? recording : null);
  const rawImgCandidate = effectivePrefer
    ? (portraitUrl || recording.cover_image)
    : (recording.cover_image || portraitUrl);
  // PERMANENT RULE (Feb 2026 – user restatement): the app treats the
  // curated cover pool as the DEFINITIVE gallery for every speaker.
  //
  //   • Pool ≥ 1 for this speaker  → ALWAYS pool (randomized per
  //                                   drive_file_id — deterministic so
  //                                   the same recording keeps the same
  //                                   cover across renders while
  //                                   siblings rotate). If the pool
  //                                   only has 1-2 pics that's fine —
  //                                   the user accepts repetition.
  //   • Pool = 0                    → Keep the recording's real
  //                                   cover_image / portrait, unless
  //                                   it's a translucent cutout URL —
  //                                   in that case we STILL rewrite
  //                                   to pool || by-file-id/original
  //                                   (Rule 16 – never render a
  //                                   translucent cutout on a solid
  //                                   backdrop).
  //   • RULE 19 exception: inside the speaker's own galleryless page
  //                        keep the cutout (their intended cover).
  const { pickCover } = useCoverPool();
  const speakerPoolCount = poolCount(recording.speaker || '');
  const rawImg = (() => {
    if (insideOwnSpeakerAndGalleryless) return rawImgCandidate;
    // Force pool for any speaker with a gallery folder.
    if (speakerPoolCount >= 1) {
      const pooled = pickCover(recording.speaker || '', recording.drive_file_id || '');
      if (pooled) return pooled;
      // fall through if pool lookup failed for any reason
    }
    const u = rawImgCandidate;
    if (!u || typeof u !== 'string') return u;
    // Cutout rewrite — never paint a translucent PNG on a color bg.
    const CUTOUT_RE = /\/api\/portrait\/cutout\/([A-Za-z0-9_-]+)/;
    const OBJ_CUTOUT_RE = /portraits-cutout(?:-\d+)?\/([A-Za-z0-9_-]+)\.png/;
    const m = u.match(CUTOUT_RE) || u.match(OBJ_CUTOUT_RE);
    if (m && m[1]) {
      const fid = m[1];
      const pooled = pickCover(recording.speaker || '', recording.drive_file_id || '');
      if (pooled) return pooled;
      return `/api/portrait/by-file-id/${fid}?size=400`;
    }
    return u;
  })();
  const hasPhoto = rawImg && typeof rawImg === 'string'
    && (rawImg.startsWith('http') || rawImg.startsWith('/api'))
    && !imageError;
  const src = hasPhoto ? smallerThumb(rawImg) : null;
  const [starred, setStarred] = useState(false);
  const [listenStatus, setListenStatus] = useState(() => getListenStatus(recording.drive_file_id));

  useEffect(() => {
    setStarred(isFavorited('recording', recording.drive_file_id));
    setListenStatus(getListenStatus(recording.drive_file_id));
    const onChange = () => setStarred(isFavorited('recording', recording.drive_file_id));
    // The resume list is written to whenever the player emits progress
    // (see AudioPlayer.jsx) — refresh listen status on `storage` events
    // AND on window focus (returning from a listen session).
    const onResume = () => setListenStatus(getListenStatus(recording.drive_file_id));
    window.addEventListener(favoritesChangeEvent, onChange);
    window.addEventListener('storage', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      window.removeEventListener(favoritesChangeEvent, onChange);
      window.removeEventListener('storage', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [recording.drive_file_id]);

  const goToPlayer = (e) => {
    e.stopPropagation();
    // When the caller hands us a section queue (Home grid, Topic page,
    // Parsha, Library, a Speaker folder…), forward it to the player via
    // router state so "next track" advances through the section — not the
    // fallback list of same-speaker recordings.
    if (Array.isArray(queue) && queue.length > 0) {
      navigate(`/player/${recording.drive_file_id}`, { state: { queue } });
    } else {
      navigate(`/player/${recording.drive_file_id}`);
    }
  };

  const handleStar = (e) => {
    e.stopPropagation();
    toggleFavorite('recording', recording);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    downloadRecording(recording);
  };

  // Feb-2026 (user "tell me why didn't figure it out … find and ban the pic"):
  // one-click ban button. Extracts the drive_id from the current cover URL and
  // POSTs to /admin/portrait/ban. Only rendered when admin passcode is unlocked.
  const passcode = (() => {
    try { return sessionStorage.getItem('vaad_admin_passcode_v1') || ''; }
    catch { return ''; }
  })();
  const banDriveId = (() => {
    if (!src || typeof src !== 'string') return null;
    // Match every URL shape pickCover / portrait / drive-thumbnail can emit.
    const patterns = [
      /\/api\/portrait\/by-file-id\/([A-Za-z0-9_-]+)/,
      /\/api\/portrait\/cutout\/([A-Za-z0-9_-]+)/,
      /\/api\/gallery\/photo\/([A-Za-z0-9_-]+)/,
      /drive\.google\.com\/thumbnail\?id=([A-Za-z0-9_-]+)/,
      /portraits-cutout(?:-\d+)?\/([A-Za-z0-9_-]+)\.(?:png|webp|jpg)/,
    ];
    for (const p of patterns) { const m = src.match(p); if (m && m[1]) return m[1]; }
    return null;
  })();
  const handleBan = async (e) => {
    e.stopPropagation();
    if (!banDriveId) return;
    if (!window.confirm(
      `Permanently ban this pic (drive_id: ${banDriveId.slice(0, 12)}…) from every card, page, and speaker gallery? Cannot be undone from here.`,
    )) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/portrait/ban`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(passcode ? { 'X-Admin-Passcode': passcode } : {}),
        },
        body: JSON.stringify({
          drive_id: banDriveId,
          reason: `From recording "${recording.title || ''}" speaker "${recording.speaker || ''}"`,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Force this card to fall back to the Music icon immediately.
      setImageError(true);
      // Tell every open card to reload — cover pool must re-filter.
      window.dispatchEvent(new Event('vaad-portrait-banned'));
      // eslint-disable-next-line no-alert
      alert(`Banned. ${data.total_bans} total bans, ${data.covers_cleared} recording covers cleared. Publish to sync to production.`);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Ban failed: ${err.message}`);
    }
  };

  return (
    <div
      className="group relative bg-white rounded-lg overflow-hidden border border-transparent hover:border-[#9a7b24]/50 transition-colors duration-200 cursor-pointer"
      data-testid="recording-card"
      onClick={goToPlayer}
      // Warm the recording payload in the SW cache the instant the
      // user's cursor/finger lands on the tile — by the time they've
      // finished tapping, /api/recordings/{id} is already loaded.
      onPointerEnter={() => prefetchRecording(recording.drive_file_id)}
      onPointerDown={() => prefetchRecording(recording.drive_file_id)}
      role="button"
      tabIndex={0}
      onFocus={() => prefetchRecording(recording.drive_file_id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToPlayer(e); } }}
    >
      {/* Cover — cutout portraits sit on the speaker's studio backdrop so
          he always appears with HIS background (matches the home tiles) */}
      <div
        className="relative w-full aspect-square bg-[#1a1410] rounded-t-lg overflow-hidden"
        style={
          typeof src === 'string' && src.includes('/api/portrait/cutout/')
            ? { background: getSpeakerPaletteScene(recording.speaker) }
            : undefined
        }
      >
        {hasPhoto ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            onError={() => setImageError(true)}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="recording-cover-image"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#2b241a] to-[#1a1410]">
            <Music className="w-10 h-10 text-[#9a7b24]/60" />
          </div>
        )}

        {/* Play button — decorative visual on desktop hover only. `pointer-events-none`
         * so the whole card handles the click; one tap on mobile navigates. */}
        <span
          className="absolute inset-0 hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity pointer-events-none"
          aria-hidden="true"
          data-testid="recording-play-overlay"
        >
          <span className="w-11 h-11 rounded-full flex items-center justify-center shadow-[0_6px_18px_rgba(0,0,0,0.35)] transition-transform duration-200 group-hover:scale-110"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #e5c976 0%, #c9a437 55%, #7d6119 100%)',
              border: '1px solid rgba(255,240,200,0.55)',
            }}
          >
            <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
          </span>
        </span>

        {/* Feb-2026 (user permanent rule): NEVER show the star, download,
            or email icons hovering on the recording card unless we're
            hovering with a mouse on DESKTOP. Mobile users open the shiur
            first — they use the actions IN the player, not from the tile.
            All three corner buttons are hidden by default; the whole
            group appears together on desktop group-hover. */}
        <button
          type="button"
          onClick={handleStar}
          className="hidden md:group-hover:flex group/star absolute top-1 left-1 w-5 h-5 rounded-full bg-black/25 hover:bg-black/55 items-center justify-center transition-colors z-10 opacity-70 hover:opacity-100"
          aria-label={starred ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={starred}
          data-testid="recording-favorite-btn"
        >
          <Star
            className={`w-2.5 h-2.5 transition-colors ${
              starred
                ? 'text-[#e0b84a] fill-[#e0b84a]'
                : 'text-white/80 group-hover/star:text-[#e0b84a] group-hover/star:fill-[#e0b84a]'
            }`}
            strokeWidth={2}
          />
        </button>

        {/* Listened status — "done" = green check top-right, "partial" =
            small % chip centered so users see progress at a glance without
            checking the resume list. */}
        {listenStatus.status === 'done' && (
          <div
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow z-10"
            data-testid="recording-listened-done"
            title="Listened"
          >
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
        )}
        {listenStatus.status === 'partial' && (
          <div
            className="absolute inset-x-0 bottom-0 h-1.5 bg-black/40 z-10"
            data-testid="recording-listened-partial"
            title={`${listenStatus.percent}% listened`}
          >
            <div
              className="h-full bg-[#e0b84a] transition-[width]"
              style={{ width: `${Math.min(100, listenStatus.percent || 0)}%` }}
            />
          </div>
        )}

        {/* Add-to-playlist icon lives on the PlayerPage (inside the current
         * recording) — not on the tile — so the card grid stays clean. Users
         * add to a playlist AFTER they're actively engaged with a shiur.
         */}

        {/* Download in the top-right corner.
         *  - Desktop: hidden until the card is hovered, then fades in.
         *  - Mobile:  always visible but tiny + low-opacity so it stays out of
         *    the way; users can tap it directly without opening the shiur. */}
        <button
          type="button"
          onClick={handleDownload}
          className="hidden md:group-hover:flex absolute top-1 right-1 rounded-full bg-black/60 hover:bg-[#9a7b24] text-white items-center justify-center transition-opacity z-10 md:w-7 md:h-7"
          aria-label="Download"
          data-testid="recording-download-btn"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        {passcode && banDriveId && hasPhoto && (
          <button
            type="button"
            onClick={handleBan}
            className="absolute bottom-1 left-1 w-6 h-6 rounded-full bg-red-600/85 hover:bg-red-700 text-white flex items-center justify-center transition-colors z-10 opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label="Ban this picture"
            title="Ban this picture (admin)"
            data-testid="recording-ban-pic-btn"
          >
            <Ban className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
        )}
        {/* Feb-2026 (user): Removed the "Email me this shiur" hover icon.
            Email is still fully accessible via the Share menu (ShareMenu
            → "Email") on the player page — just not cluttering every
            recording tile on hover. */}
      </div>

      <div className="px-2.5 py-2">
        <h3 className="text-[#0f1729] text-[13px] font-medium leading-snug line-clamp-2 min-h-[2.5em]">
          {recording.title}
        </h3>
        <p className="text-[#4b5563] text-[11px] mt-0.5 truncate">{recording.speaker}</p>
      </div>
    </div>
  );
};

export default RecordingCard;
